import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { onDutyStatuses, type IState } from "../helpers";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type WorkdayDutyConditions = {
    avail: number | undefined;
    isOnDuty: boolean;
    splitSBInProgress: boolean;
    /** A break anchor exists; true from the instant the break opens (consecutiveOffStartTime <= nextDate). */
    breakInProgress: boolean;
    dutyLimit: number | undefined;
};

export class WorkdayDutyCalculator extends AvailabilityCalculator {
    private conditions(state: IState): WorkdayDutyConditions {
        const avail = discreteAvailabilityFunctions.Duty(state, false);
        const isOnDuty = onDutyStatuses.indexOf(state.current?.status) !== -1;
        const splitSBInProgress = !!(state.workday.splitSB?.inSleeper || state.workday.propertySplitSB?.inSleeper);
        // <= as in workday.ts: countOffDutyTime sets the anchor when the break opens, so it is
        // already stable at that log boundary.
        const breakInProgress = state.workday.consecutiveOffStartTime !== undefined
            && state.workday.consecutiveOffStartTime.getTime() <= state.nextDate.getTime();
        const dutyLimit = state.workday.dutyLimit ?? state.config.workday?.dutyMaximum;

        return { avail, isOnDuty, splitSBInProgress, breakInProgress, dutyLimit };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        return this.conditions(state).isOnDuty;
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined || !c.isOnDuty) return undefined;

        // Guard avail > 0: raw value may be ≤ 0 when already in violation — no future
        // zero-crossing exists in that case.
        if (c.avail > 0) {
            return new Date(state.nextDate.getTime() + c.avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        if (c.isOnDuty) {
            // Driver on-duty; workday duty availability decreasing at rate −1.
            // No genuine inflections predicted while on-duty; exhaustion is handled separately.
            return {};
        }

        // SB split takes priority: the workday resets at split completion, which is not predictable
        // from the offDutyMinimum threshold.
        if (c.splitSBInProgress) {
            return {
                UNKNOWN_splitSb: { factor: () => state.nextDate, label: "coverage gap" },
            };
        }

        // Off duty, Duty is frozen: only countOnDutyTime moves it. The workday reset is the one
        // time-based inflection, and it changes Duty only if duty has been accumulated — with
        // avail already at dutyLimit the reset puts Duty back where it is.
        const hasDutyAccumulated = c.dutyLimit !== undefined && c.avail < c.dutyLimit;
        if (!c.breakInProgress || !hasDutyAccumulated) {
            return {};
        }

        // Asymmetric (property) rulesets: a property split can complete out of plain off-duty
        // time and reset the workday before the threshold, and splitSBInProgress cannot see it at
        // the instant the break opens because inSleeper only flips once time has accrued.
        const breakOpensNow = state.workday.consecutiveOffStartTime!.getTime() === state.nextDate.getTime();
        if (breakOpensNow && state.config.splitConfig?.type === "asymmetric") {
            return {
                UNKNOWN_noAnchor: { factor: () => state.nextDate, label: "coverage gap" },
            };
        }

        // A pending ExemptionReducedRest log that applies to this break resets the workday when
        // the break reaches the reduced minimum (updateEligibleReducedRestPeriods closes the
        // eligible period, and offDutyMinimum is decremented to it).
        const reducedRestResetDate = this.reducedRestResetDate(state);
        if (reducedRestResetDate) {
            return {
                workdayResetErr: { factor: () => reducedRestResetDate, label: "workday reset" },
            };
        }

        const resetDate = this.workdayResetDate(state);
        if (resetDate) {
            return {
                workdayReset: { factor: () => resetDate, label: "workday reset" },
            };
        }
        return {};
    }
}

export const extractWorkdayDutyConditions = (state: IState): WorkdayDutyConditions => {
    return new WorkdayDutyCalculator().extractConditions(state) as WorkdayDutyConditions;
};

export const computeWorkdayDutyResult = (state: IState): TComputedAvailabilityResult | undefined => {
    return new WorkdayDutyCalculator().compute(state);
};
